import * as Sentry from '@sentry/browser';
import type {
  DateFilterModel,
  IMultiFilterModel,
  IServerSideGetRowsRequest,
  NumberFilterModel,
  SetFilterModel,
  TextFilterModel,
} from 'ag-grid-enterprise';
import { isEmpty, omitBy } from 'lodash-es';
import { Temporal } from 'temporal-polyfill';
import type { z } from 'zod/v4';
import type { CustomSetFilterModel } from '~/components/data-grid/data-grid-filters';

/* -------------------------------------------------------------------------------------------------
 * Type guards
 * -----------------------------------------------------------------------------------------------*/

export type AgGridFilterModel =
  | SetFilterModel
  | TextFilterModel
  | NumberFilterModel
  | DateFilterModel
  | CustomSetFilterModel
  | IMultiFilterModel;

export type UrlQueryParamPrimitive = boolean | number | string;
export type UrlQueryParams = Readonly<
  Record<string, UrlQueryParamPrimitive | ReadonlyArray<UrlQueryParamPrimitive> | undefined>
>;

type AgGridFilterConversionOptions = {
  filterModelKeyMap?: Readonly<Record<string, string>>;
};

export const isDateFilter = (filter: AgGridFilterModel): filter is DateFilterModel => filter.filterType === 'date';
export const isSetFilter = (filter: AgGridFilterModel): filter is SetFilterModel => filter.filterType === 'set';
export const isTextFilter = (filter: AgGridFilterModel): filter is TextFilterModel => filter.filterType === 'text';
export const isNumberFilter = (filter: AgGridFilterModel): filter is NumberFilterModel =>
  filter.filterType === 'number';
export const isCustomFilter = (filter: AgGridFilterModel): filter is CustomSetFilterModel =>
  filter.filterType === 'customSet';
export const isMultiFilter = (filter: AgGridFilterModel): filter is IMultiFilterModel => filter.filterType === 'multi';

/**
 * AG Grid date filters store bounds as date-only or no-zone datetime strings, e.g.
 * `2026-01-21`, `2026-01-21 00:00:00`, or `2026-01-21T00:00:00`.
 */
export const parseAgGridPlainDate = (value: string | null | undefined): Temporal.PlainDate | undefined => {
  if (!value) {
    return undefined;
  }

  const datePart = value.match(/^(\d{4}-\d{2}-\d{2})(?:[ T]\d{2}:\d{2}:\d{2})?$/)?.[1];
  if (!datePart) {
    return undefined;
  }

  try {
    return Temporal.PlainDate.from(datePart);
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

/* -------------------------------------------------------------------------------------------------
 * Normalization functions
 * -----------------------------------------------------------------------------------------------*/

export type PlainDateRange = { from: Temporal.PlainDate; to: Temporal.PlainDate };

/**
 * Reads an inclusive `inRange` date filter, as emitted by `DateRangeShortcutFilter`, into plain-date bounds.
 */
export const dateFilterToPlainDateRange = (filter: AgGridFilterModel | undefined): PlainDateRange | undefined => {
  if (!filter || !isDateFilter(filter) || filter.type !== 'inRange') {
    return undefined;
  }

  const from = parseAgGridPlainDate(filter.dateFrom);
  const to = parseAgGridPlainDate(filter.dateTo);
  return from && to ? { from, to } : undefined;
};

// We don't support the AG-Grid filter model directly in our API
// so we need to extract the filter values from the filter model
const extractFilterValue = (filter: AgGridFilterModel): unknown => {
  if (isCustomFilter(filter)) {
    return { values: filter.values };
  }

  if (isSetFilter(filter)) {
    return filter.values.filter((v) => v != null);
  }

  if (isDateFilter(filter)) {
    return parseAgGridPlainDate(filter.dateFrom);
  }

  if (isTextFilter(filter) || isNumberFilter(filter)) {
    return filter.filter;
  }

  return undefined;
};

export const recordToUrlSearchParams = <T extends Record<string, unknown>>(data: T): URLSearchParams => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }

  return params;
};

export const agGridFiltersToRecord = <T extends z.ZodTypeAny>(
  filterModel: IServerSideGetRowsRequest['filterModel'],
  queryParamsSchema: T,
  { filterModelKeyMap = {} }: AgGridFilterConversionOptions = {},
): z.infer<T> | undefined => {
  if (!filterModel || isEmpty(filterModel)) {
    return undefined;
  }

  const filtersToApply = omitBy(
    filterModel as Record<string, AgGridFilterModel>,
    (value) => isCustomFilter(value) && value.hasAllSelected,
  );

  if (isEmpty(filtersToApply)) {
    return undefined;
  }

  const flatParams: Record<string, unknown> = {};
  for (const [key, filter] of Object.entries(filtersToApply)) {
    if (filter && typeof filter === 'object') {
      flatParams[filterModelKeyMap[key] ?? key] = extractFilterValue(filter);
    }
  }

  const result = queryParamsSchema.safeParse(flatParams);
  if (!result.success) {
    return undefined;
  }

  return result.data;
};

export const filtersToSearchParams = <T extends z.ZodTypeAny>(input: z.infer<T>, schema: T): URLSearchParams => {
  const result = schema.safeParse(input);
  if (!result.success) {
    Sentry.captureException(result.error);
    return new URLSearchParams();
  }

  return recordToUrlSearchParams(result.data as Record<string, unknown>);
};
